import { useMemo } from 'react';

import type { RosterEmployee } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import type { User } from '@/types/directory/User';
import type { EmployeeCompetencyAssignment } from '@/types/performance-profile';

import { useGetRoster } from '@/api/competency-services/profile-assignment';
import { isValidProfileId } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import { buildRosterEmployee } from '@/components/CompetencyProfileAssignment';

interface UseRosterEmployeeParams {
    employeeId: string | undefined;
    /** The employee's directory entry, which stands in for the roster row until the roster has one. */
    employee: User | undefined;
    assignment: EmployeeCompetencyAssignment | undefined;
    /** Off, the roster is not read and no employee is built from it. */
    enabled: boolean;
}

/**
 * The profile the employee is assigned: the performance-profile read model first, so a profile
 * saved a moment ago shows even while the roster is still being read again.
 */
const readAssignedProfileId = (
    assignment: EmployeeCompetencyAssignment | undefined,
    fromRoster: RosterEmployee | undefined,
): string | null => {
    if (isValidProfileId(assignment?.competencyProfileId)) {
        return assignment.competencyProfileId;
    }
    if (isValidProfileId(fromRoster?.assignedProfileId)) {
        return fromRoster.assignedProfileId;
    }
    return null;
};

/**
 * The employee as the competency-profile assignment screens read them, and the colleagues whose
 * profile can be copied onto them.
 */
export const useRosterEmployee = ({
    employeeId,
    employee,
    assignment,
    enabled,
}: UseRosterEmployeeParams) => {
    const { data: roster } = useGetRoster(
        { isActiveOnly: true, includeInvited: true },
        Boolean(employeeId) && enabled,
    );

    const rosterEmployee = useMemo((): RosterEmployee | null => {
        if (!employeeId) {
            return null;
        }

        const fromRoster = (roster ?? []).find((candidate) => candidate.id === employeeId);
        const assignedProfileId = readAssignedProfileId(assignment, fromRoster);

        if (fromRoster) {
            return {
                ...fromRoster,
                assignedProfileId,
                assignedProfileName: assignedProfileId
                    ? (assignment?.profileName ?? fromRoster.assignedProfileName)
                    : null,
            };
        }

        if (!employee) {
            return null;
        }

        return buildRosterEmployee(
            {
                id: employeeId,
                firstName: employee.firstName,
                lastName: employee.lastName,
                emailAddress: employee.emailAddress,
                profilePictureUrl: employee.profilePictureUrl,
                departmentId: employee.departmentId,
                jobTitleId: employee.jobTitleId,
                employeeLevelId: employee.employeeLevelId,
                companyAddressId: employee.companyAddressId,
                isActive: true,
            },
            {
                assignedProfileId,
                assignedProfileName: assignedProfileId ? (assignment?.profileName ?? null) : null,
            },
        );
    }, [employeeId, roster, employee, assignment]);

    const copyCandidates = useMemo(
        () =>
            (roster ?? []).filter(
                (candidate) => candidate.id !== employeeId && Boolean(candidate.assignedProfileId),
            ),
        [roster, employeeId],
    );

    return { rosterEmployee, copyCandidates };
};
